import { useCallback, useEffect, useRef, useState, createContext, useContext } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Json } from "@/integrations/supabase/types";

type InteractionType = "page_view" | "click" | "feature_usage" | "navigation";

interface PendingInteraction {
  interaction_type: InteractionType;
  target: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

interface DeviceInfo {
  isMobile: boolean;
  screenWidth: number;
  screenHeight: number;
  userAgent: string;
}

const generateSessionId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};

const getDeviceInfo = (): DeviceInfo => ({
  isMobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
  screenWidth: window.innerWidth,
  screenHeight: window.innerHeight,
  userAgent: navigator.userAgent,
});

const SESSION_STORAGE_KEY = "nimi_session_id";
const FLUSH_INTERVAL = 2000;

/**
 * Hook for tracking user interactions.
 * @param authUserId - The authenticated user's ID from AuthProvider (null if not logged in)
 * @param authLoading - Whether auth state is still loading
 */
export const useInteractionTracking = (authUserId: string | null, authLoading: boolean = false) => {
  const [sessionId, setSessionId] = useState<string>("");
  const sessionIdRef = useRef<string>("");
  const pendingInteractions = useRef<PendingInteraction[]>([]);
  const flushTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionInitialized = useRef(false);
  const isMounted = useRef(false);
  // Store authUserId in a ref so flushInteractions always has the latest value
  const userIdRef = useRef<string | null>(authUserId);
  // Store authLoading in a ref for flush logic
  const authLoadingRef = useRef<boolean>(authLoading);

  // Keep refs in sync
  useEffect(() => {
    userIdRef.current = authUserId;
  }, [authUserId]);

  useEffect(() => {
    authLoadingRef.current = authLoading;
  }, [authLoading]);

  // Initialize session - only after mount
  useEffect(() => {
    isMounted.current = true;
    
    const initSession = () => {
      if (!isMounted.current) return;
      
      try {
        let storedSessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (!storedSessionId) {
          storedSessionId = generateSessionId();
          sessionStorage.setItem(SESSION_STORAGE_KEY, storedSessionId);
        }
        
        if (!isMounted.current) return;
        sessionIdRef.current = storedSessionId;
        setSessionId(storedSessionId);

        if (!sessionInitialized.current && isMounted.current) {
          sessionInitialized.current = true;
          const deviceInfo = getDeviceInfo() as unknown as Json;
          
          void supabase.from("user_sessions").upsert(
            [{
              session_id: storedSessionId,
              user_id: userIdRef.current,
              device_info: deviceInfo,
              started_at: new Date().toISOString(),
              last_activity_at: new Date().toISOString(),
            }],
            { onConflict: "session_id" }
          );
        }
      } catch {
        // Silently fail
      }
    };

    initSession();
    
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Update session and backfill interactions with user_id when auth resolves
  useEffect(() => {
    if (authUserId && sessionId) {
      // Update session with user_id
      supabase
        .from("user_sessions")
        .update({ user_id: authUserId })
        .eq("session_id", sessionId)
        .then(({ error }) => {
          if (error) console.error("Failed to update session with user_id:", error);
        });

      // Backfill any interactions from this session that have null user_id
      supabase
        .from("user_interactions")
        .update({ user_id: authUserId })
        .eq("session_id", sessionId)
        .is("user_id", null)
        .then(({ error }) => {
          if (error) console.error("Failed to backfill interactions with user_id:", error);
        });
    }
  }, [authUserId, sessionId]);

  // Flush pending interactions - uses refs for current values
  // Also updates user's last_active_at in profiles table
  const flushInteractions = useCallback(async () => {
    const currentSessionId = sessionIdRef.current;
    
    if (pendingInteractions.current.length === 0 || !currentSessionId) return;
    
    // If auth is still loading, reschedule the flush for later
    if (authLoadingRef.current) {
      flushTimeoutRef.current = setTimeout(() => {
        void flushInteractions();
      }, FLUSH_INTERVAL);
      return;
    }

    const interactionsToFlush = [...pendingInteractions.current];
    pendingInteractions.current = [];

    try {
      // Get current userId from ref at flush time
      const currentUserId = userIdRef.current;
      
      const records = interactionsToFlush.map((interaction) => ({
        session_id: currentSessionId,
        user_id: currentUserId,
        interaction_type: interaction.interaction_type,
        target: interaction.target,
        metadata: interaction.metadata as unknown as Json,
        created_at: interaction.created_at,
      }));

      const { error } = await supabase.from("user_interactions").insert(records);
      if (error) console.error("[Tracking] Insert error:", error);

      // Update last_active_at for logged-in users
      if (currentUserId) {
        (async () => {
          try {
            const { error } = await supabase
              .from("profiles")
              .update({ last_active_at: new Date().toISOString() })
              .eq("id", currentUserId)
              .select("id")
              .single();
            
            if (error) {
              console.error("[Tracking] Failed to update last_active_at:", error);
            }
          } catch (e) {
            console.error("[Tracking] last_active_at update exception:", e);
          }
        })();
      }
    } catch (e) {
      console.error("[Tracking] Flush error:", e);
    }
  }, []);

  const scheduleFlush = useCallback(() => {
    if (flushTimeoutRef.current) {
      clearTimeout(flushTimeoutRef.current);
    }
    flushTimeoutRef.current = setTimeout(flushInteractions, FLUSH_INTERVAL);
  }, [flushInteractions]);

  useEffect(() => {
    return () => {
      if (flushTimeoutRef.current) {
        clearTimeout(flushTimeoutRef.current);
      }
      flushInteractions();
    };
  }, [flushInteractions]);

  const addInteraction = useCallback(
    (type: InteractionType, target: string, metadata: Record<string, unknown> = {}) => {
      if (!isMounted.current) return;
      pendingInteractions.current.push({
        interaction_type: type,
        target,
        metadata,
        created_at: new Date().toISOString(),
      });
      scheduleFlush();
    },
    [scheduleFlush]
  );

  const trackClick = useCallback(
    (elementName: string, context?: Record<string, unknown>) => {
      addInteraction("click", elementName, {
        page: typeof window !== "undefined" ? window.location.pathname : "",
        ...context,
      });
    },
    [addInteraction]
  );

  const trackPageView = useCallback(
    (path: string, metadata?: Record<string, unknown>) => {
      addInteraction("page_view", path, {
        ...metadata,
      });
    },
    [addInteraction]
  );

  const trackFeatureUsage = useCallback(
    (featureName: string, action: string, metadata?: Record<string, unknown>) => {
      addInteraction("feature_usage", featureName, {
        action,
        page: typeof window !== "undefined" ? window.location.pathname : "",
        ...metadata,
      });
    },
    [addInteraction]
  );

  const trackNavigation = useCallback(
    (from: string, to: string, method: "click" | "back" | "forward" | "direct") => {
      addInteraction("navigation", `${from} -> ${to}`, { from, to, method });
    },
    [addInteraction]
  );

  return { sessionId, trackClick, trackPageView, trackFeatureUsage, trackNavigation };
};

// Context
interface InteractionTrackingContextType {
  trackClick: (elementName: string, context?: Record<string, unknown>) => void;
  trackPageView: (path: string, metadata?: Record<string, unknown>) => void;
  trackFeatureUsage: (featureName: string, action: string, metadata?: Record<string, unknown>) => void;
  trackNavigation: (from: string, to: string, method: "click" | "back" | "forward" | "direct") => void;
}

export const InteractionTrackingContext = createContext<InteractionTrackingContextType | null>(null);

export const useTracking = () => {
  const context = useContext(InteractionTrackingContext);
  if (!context) {
    return {
      trackClick: () => {},
      trackPageView: () => {},
      trackFeatureUsage: () => {},
      trackNavigation: () => {},
    };
  }
  return context;
};
